/**
 * Звуки интерфейса — синтезом через Web Audio.
 *
 * Почему не файлы: набор коротких звуков в бандле это лишние десятки килобайт
 * и отдельные запросы, а в Mini App на мобильном интернете каждый килобайт
 * бьёт по времени до первой игры. Синтез весит пару килобайт кода, звучит
 * одинаково на всех устройствах и не требует сети.
 *
 * Важно про автозапуск: браузеры не дают создать звук до первого касания
 * экрана. Поэтому контекст создаётся лениво и «размораживается» на первом
 * жесте пользователя.
 */

const STORAGE_KEY = "minigames:muted";

let ctx: AudioContext | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(value: boolean): void {
  muted = value;
  try {
    localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  } catch {
    /* приватный режим — переживём */
  }
}

function audio(): AudioContext | null {
  if (muted) return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  // После сворачивания приложения контекст засыпает.
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Вызвать на первом касании — иначе первый же звук будет проглочен. */
export function unlockAudio(): void {
  const c = audio();
  if (c && c.state === "suspended") void c.resume();
}

interface ToneOptions {
  freq: number;
  /** Длительность, с. */
  duration: number;
  type?: OscillatorType;
  /** Громкость 0..1. */
  gain?: number;
  /** Задержка от «сейчас», с. */
  delay?: number;
  /** Плавно съехать к этой частоте — придаёт звуку характер. */
  slideTo?: number;
}

function tone({ freq, duration, type = "sine", gain = 0.18, delay = 0, slideTo }: ToneOptions): void {
  const c = audio();
  if (!c) return;

  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const amp = c.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + duration);

  // Мгновенная атака и мягкий спад: щелчок без «клика» на срезе.
  amp.gain.setValueAtTime(0.0001, t0);
  amp.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
  amp.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  osc.connect(amp).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

/** Короткий шумовой щелчок — «деревянный» стук фигуры о доску. */
function knock(gain = 0.16, delay = 0): void {
  const c = audio();
  if (!c) return;

  const t0 = c.currentTime + delay;
  const frames = Math.floor(c.sampleRate * 0.05);
  const buffer = c.createBuffer(1, frames, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) {
    // Затухающий шум даёт удар без тона.
    data[i] = (Math.random() * 2 - 1) * (1 - i / frames) ** 3;
  }

  const src = c.createBufferSource();
  src.buffer = buffer;

  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(1400, t0);

  const amp = c.createGain();
  amp.gain.setValueAtTime(gain, t0);

  src.connect(filter).connect(amp).connect(c.destination);
  src.start(t0);
}

export const sound = {
  /** Свой или чужой ход по доске. */
  move: () => knock(0.14),
  /** Взятие — резче и с призвуком. */
  capture: () => {
    knock(0.2);
    tone({ freq: 180, duration: 0.12, type: "triangle", gain: 0.1 });
  },
  /** Карта легла на стол. */
  card: () => knock(0.1),
  /** Что-то выбрано. */
  select: () => tone({ freq: 660, duration: 0.05, type: "sine", gain: 0.06 }),
  /** Соперник вошёл, партия началась. */
  start: () => {
    tone({ freq: 523, duration: 0.14, gain: 0.14 });
    tone({ freq: 784, duration: 0.2, gain: 0.14, delay: 0.1 });
  },
  win: () => {
    tone({ freq: 523, duration: 0.14, gain: 0.16 });
    tone({ freq: 659, duration: 0.14, gain: 0.16, delay: 0.12 });
    tone({ freq: 784, duration: 0.28, gain: 0.16, delay: 0.24 });
  },
  lose: () => {
    tone({ freq: 392, duration: 0.18, gain: 0.14, type: "triangle" });
    tone({ freq: 262, duration: 0.34, gain: 0.14, type: "triangle", delay: 0.15 });
  },
  draw: () => {
    tone({ freq: 440, duration: 0.16, gain: 0.13 });
    tone({ freq: 440, duration: 0.24, gain: 0.13, delay: 0.16 });
  },
  /** Пришло сообщение или реакция. */
  notify: () => tone({ freq: 880, duration: 0.09, gain: 0.09, slideTo: 1200 }),
  /** Время на исходе — тикает на последних секундах. */
  tick: () => tone({ freq: 1000, duration: 0.04, gain: 0.07 }),
};
